import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { EngineService } from '@/services/engine';
import { useSourcesStore } from '@/stores';
import { getSourceCacheKey } from '@/constants/cache-keys';
import { MILLISECONDS_TO_SECONDS } from '@/constants/time';
import {
    EventViewErrorReason,
    EventViewStatus,
    type Event,
} from '@/types/event';
import type { FullSource } from '@/types/source';
import type { BoundingBoxData, EventPlaybackRange } from '@/types/video-player';
import { buildBoundingBoxProps } from '@/utils/bounding-box';
import {
    deriveEventPlaybackRange,
    isEventStillProcessing,
} from '@/utils/event-playback-range';
import { isNotFoundError } from '@/utils/http';
import { useReadinessDeadline } from './useReadinessDeadline';

export type EventViewState =
    | { status: EventViewStatus.LOADING }
    | {
          status: EventViewStatus.ERROR;
          reason: EventViewErrorReason;
          retry: () => void;
      }
    | {
          status: EventViewStatus.READY;
          source: FullSource;
          eventRange: EventPlaybackRange;
          boundingBoxProps: BoundingBoxData | undefined;
      };

interface UseEventReadyParams {
    event: Event;
    focuses: number[][];
    refetchEvent?: () => void;
}

export function useEventReady({
    event,
    focuses,
    refetchEvent,
}: UseEventReadyParams): EventViewState {
    const { getSourceById, addSource } = useSourcesStore();
    const cachedSource = getSourceById(event.sourceId);
    const startedWhileProcessingRef = useRef(false);
    const [retryAttempt, setRetryAttempt] = useState(0);

    const {
        data: fetchedSource,
        error: sourceError,
        mutate: revalidateSource,
    } = useSWR<FullSource>(
        !cachedSource ? getSourceCacheKey(event.sourceId) : null,
        () => EngineService.getSource(event.sourceId),
        { shouldRetryOnError: (err) => !isNotFoundError(err) },
    );

    useEffect(() => {
        if (fetchedSource) {
            addSource(fetchedSource);
        }
    }, [fetchedSource, addSource]);

    const source = cachedSource ?? fetchedSource;
    const eventRange = source
        ? deriveEventPlaybackRange({
              event,
              sourceType: source.type,
              focuses,
          })
        : null;
    const ready = Boolean(source && eventRange);

    const sourceMissing = isNotFoundError(sourceError);
    const timedOut = useReadinessDeadline(
        !ready && !sourceMissing,
        `${event.id}:${retryAttempt}`,
    );

    useEffect(() => {
        if (ready) {
            startedWhileProcessingRef.current = false;
            return;
        }
        startedWhileProcessingRef.current = isEventStillProcessing(
            event,
            Date.now() / MILLISECONDS_TO_SECONDS,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, event.id]);

    if (source && eventRange) {
        return {
            status: EventViewStatus.READY,
            source,
            eventRange,
            boundingBoxProps: buildBoundingBoxProps({ event, focuses }),
        };
    }

    const retry = () => {
        setRetryAttempt((attempt) => attempt + 1);
        void revalidateSource();
        refetchEvent?.();
    };

    if (sourceMissing) {
        return {
            status: EventViewStatus.ERROR,
            reason: EventViewErrorReason.NOT_FOUND,
            retry,
        };
    }

    if (timedOut) {
        return {
            status: EventViewStatus.ERROR,
            reason: sourceError
                ? EventViewErrorReason.FAILED
                : startedWhileProcessingRef.current
                  ? EventViewErrorReason.PROCESSING
                  : EventViewErrorReason.TIMEOUT,
            retry,
        };
    }

    return { status: EventViewStatus.LOADING };
}
